/**
 * @file User material analysis — resolve which result table is shown in the UI.
 */

import type { MaterialAnalysisSubmission } from "../../../../../services/materialAnalysisService";

export const VISIBLE_RESULT_POOL_TITLES = {
  specific: "Your Specific Data Pool",
  reliable: "Reliable Data Pool",
  total: "Total Data Pool",
} as const;

export type VisibleResultPool = keyof typeof VISIBLE_RESULT_POOL_TITLES;

export interface VisibleResultTable {
  pool: VisibleResultPool;
  title: string;
  submissions: MaterialAnalysisSubmission[];
}

/** Matches SubmissionsDataTable visibility: one pool table at a time. */
export function getVisibleResultTable(args: {
  dataSpecificityApplied: boolean;
  materialSubmissions: MaterialAnalysisSubmission[];
  reliableSubmissions: MaterialAnalysisSubmission[];
  dataSpecificitySubmissions: MaterialAnalysisSubmission[];
}): VisibleResultTable | null {
  const {
    dataSpecificityApplied,
    materialSubmissions,
    reliableSubmissions,
    dataSpecificitySubmissions,
  } = args;

  if (dataSpecificityApplied) {
    return {
      pool: "specific",
      title: VISIBLE_RESULT_POOL_TITLES.specific,
      submissions: dataSpecificitySubmissions,
    };
  }

  if (reliableSubmissions.length > 0) {
    return {
      pool: "reliable",
      title: VISIBLE_RESULT_POOL_TITLES.reliable,
      submissions: reliableSubmissions,
    };
  }

  if (materialSubmissions.length > 0) {
    return {
      pool: "total",
      title: VISIBLE_RESULT_POOL_TITLES.total,
      submissions: materialSubmissions,
    };
  }

  return null;
}

export function shouldShowResultTableSection(
  visibleTable: VisibleResultTable | null,
  dataSpecificityApplied: boolean
): boolean {
  if (!visibleTable) return false;
  return dataSpecificityApplied || visibleTable.submissions.length > 0;
}
